import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MessagesSquare, Send, ArrowLeft, Flag, Ban, Plus, X, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loading, PageHead, Empty } from "@/components/common";
import ReportModal from "@/components/ReportModal";

function NewMessageModal({ onClose, onPick }) {
  const [follows, setFollows] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/following").then((d) => setFollows(d.sellers || [])).catch(() => setFollows([])); }, []);
  const list = (follows || []).filter((f) => f.name?.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="ss-modal-back" onClick={onClose}>
      <div className="ss-modal" onClick={(e) => e.stopPropagation()} data-testid="new-message-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <h2 style={{ fontSize: 20 }}>New message</h2>
          <button type="button" className="disc-search" style={{ width: 34, height: 34 }} onClick={onClose} data-testid="new-message-close"><X size={18} /></button>
        </div>
        <div className="ss-field" style={{ height: 44, marginBottom: 12 }}><Search size={16} className="ss-muted" /><input placeholder="Search people you follow…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="new-message-search" /></div>
        {follows === null ? <Loading /> : list.length === 0 ? (
          <div className="ss-muted" style={{ fontSize: 14, padding: "20px 4px", textAlign: "center" }} data-testid="new-message-empty">
            {follows.length === 0 ? "You're not following anyone yet. Follow sellers to message them." : "No matches."}
          </div>
        ) : (
          <div style={{ maxHeight: 380, overflowY: "auto" }}>
            {list.map((f) => (
              <div key={f.user_id} className="ss-user-row" style={{ cursor: "pointer" }} onClick={() => onPick(f.user_id)} data-testid={`new-message-user-${f.user_id}`}>
                <img className="ss-avatar" style={{ width: 40, height: 40 }} src={f.avatar} alt="" />
                <div style={{ fontWeight: 700 }}>{f.name}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Messages() {
  const { otherId } = useParams();
  const navigate = useNavigate();
  const [threads, setThreads] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [reportTarget, setReportTarget] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const endRef = useRef(null);

  const loadThreads = () => api.get("/messages/threads").then((d) => setThreads(d.threads)).catch(() => setThreads([]));
  const loadThread = (id) => api.get(`/messages/${id}`).then(setThread).catch(() => setThread(null));

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => { if (otherId) { setThread(null); loadThread(otherId); } else setThread(null); }, [otherId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !otherId) return;
    try {
      await api.post("/messages", { to: otherId, text: text.trim() });
      setText("");
      await loadThread(otherId);
      loadThreads();
    } catch (err) { toast.error(err.message); }
  };

  const block = async () => {
    if (!window.confirm(`Block ${thread.other.name}? They can no longer message you and you won't see their DMs.`)) return;
    try { await api.post(`/blocks/${otherId}`); toast.success(`Blocked ${thread.other.name}`); await loadThread(otherId); loadThreads(); }
    catch (e) { toast.error(e.message); }
  };
  const unblock = async () => {
    try { await api.del(`/blocks/${otherId}`); toast.success(`Unblocked ${thread.other.name}`); await loadThread(otherId); loadThreads(); }
    catch (e) { toast.error(e.message); }
  };

  if (threads === null) return <Loading />;

  return (
    <div className="fade-up">
      {reportTarget && <ReportModal target={reportTarget} onClose={() => setReportTarget(null)} />}
      {newOpen && <NewMessageModal onClose={() => setNewOpen(false)} onPick={(uid) => { setNewOpen(false); navigate(`/messages/${uid}`); }} />}
      <PageHead title="Messages" sub="Chat with buyers and sellers." />
      <div className="msg-layout">
        {/* Threads list */}
        <div className="ss-card" style={{ overflow: "hidden", height: 560, display: "flex", flexDirection: "column" }}>
          <div style={{ padding: 14, borderBottom: "1px solid var(--line)", fontWeight: 800, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Conversations</span>
            <button className="ss-btn ss-btn-primary ss-btn-sm" onClick={() => setNewOpen(true)} data-testid="new-message-button"><Plus size={14} /> New</button>
          </div>
          <div style={{ overflowY: "auto", flex: 1 }} data-testid="thread-list">
            {threads.length === 0 ? (
              <div className="ss-muted" style={{ padding: 20, fontSize: 14 }}>No conversations yet.</div>
            ) : threads.map((t) => (
              <div
                key={t.other_id}
                onClick={() => navigate(`/messages/${t.other_id}`)}
                className="ss-user-row"
                style={{ borderRadius: 0, padding: "12px 14px", background: otherId === t.other_id ? "var(--surface-3)" : "transparent" }}
                data-testid={`thread-${t.other_id}`}
              >
                <img className="ss-avatar" style={{ width: 40, height: 40, borderColor: "var(--line)" }} src={t.other_avatar} alt="" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, display: "flex", justifyContent: "space-between" }}>
                    <span>{t.other_name}</span>
                    {t.unread > 0 && <span className="pill-fill" style={{ background: "var(--red)", color: "#fff" }}>{t.unread}</span>}
                  </div>
                  <div className="ss-muted" style={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.last_text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Thread view */}
        <div className="chat-box" style={{ height: 560 }} data-testid="message-thread">
          {!otherId ? (
            <Empty icon={MessagesSquare} title="Select a conversation" sub="Or message a seller from a show or an order." />
          ) : !thread ? (
            <Loading />
          ) : (
            <>
              <div style={{ padding: 14, borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", gap: 10 }}>
                <button className="disc-search" style={{ width: 34, height: 34 }} onClick={() => navigate("/messages")} data-testid="thread-back"><ArrowLeft size={16} /></button>
                <img src={thread.other.avatar} alt="" style={{ width: 34, height: 34, borderRadius: 999, objectFit: "cover" }} />
                <span style={{ fontWeight: 800, flex: 1 }}>{thread.other.name}</span>
                <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => setReportTarget({ id: thread.other.user_id, name: thread.other.name, context: "message" })} data-testid="message-report" title="Report user"><Flag size={15} /></button>
                {thread.blocked ? (
                  <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={unblock} data-testid="message-unblock">Unblock</button>
                ) : (
                  <button className="ss-btn ss-btn-ghost ss-btn-sm" style={{ color: "var(--red)" }} onClick={block} data-testid="message-block" title="Block user"><Ban size={15} /></button>
                )}
              </div>
              {thread.blocked ? (
                <div className="chat-msgs" style={{ display: "grid", placeItems: "center" }}>
                  <div style={{ textAlign: "center", color: "var(--muted)" }} data-testid="blocked-banner">
                    <Ban size={28} />
                    <div style={{ fontWeight: 800, marginTop: 8 }}>You blocked {thread.other.name}</div>
                    <div style={{ fontSize: 13 }}>Their messages are hidden and they can't contact you.</div>
                    <button className="ss-btn ss-btn-ghost ss-btn-sm" style={{ marginTop: 12 }} onClick={unblock}>Unblock</button>
                  </div>
                </div>
              ) : (
              <div className="chat-msgs">
                {thread.messages.length === 0 && <div className="ss-muted" style={{ textAlign: "center", marginTop: 20 }}>Say hello 👋</div>}
                {thread.messages.map((m) => {
                  const mine = m.from_id === thread.me;
                  return (
                    <div key={m.id} style={{ alignSelf: mine ? "flex-end" : "flex-start", maxWidth: "72%" }}>
                      <div style={{ background: mine ? "var(--brand)" : "var(--surface-3)", color: mine ? "#fff" : "var(--text)", padding: "9px 13px", borderRadius: 14, fontSize: 14 }}>
                        {m.text}
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              )}
              {!thread.blocked && (
              <form onSubmit={send} style={{ padding: 12, borderTop: "1px solid var(--line)", display: "flex", gap: 8 }}>
                <div className="ss-field" style={{ flex: 1, height: 44 }}>
                  <input placeholder="Type a message…" value={text} onChange={(e) => setText(e.target.value)} data-testid="message-input" />
                </div>
                <button className="ss-btn ss-btn-primary" style={{ height: 44, padding: "0 14px" }} data-testid="message-send"><Send size={16} /></button>
              </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
